/* global HSCore */

import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import toast from "react-hot-toast";
import UserContext from "../../../utils/Contexts/UserContext";
import LanguageContext from "../../../utils/Contexts/LanguageContext";
import axiosInstance from "../../../utils/Requests/axios";
import DeleteIcon from "../../svg/Delete";
import EditIcon from "../../svg/Edit";
import Constant from "../../Constants";
import { Loader } from "../../Common";
import WriteYourself from "../../svg/WriteYourself";
import WriteWithAi from "../../svg/WriteWithAi";
import ThemeContext from "../../../utils/Contexts/ThemeContext";
import _ from "lodash";
import VidIcon from "../../svg/vidIcon";

function GuestPosts({
  guestPosts,
  guestTotal,
  getCartData,
  emptyContentError,
  handleDeleteCartItem,
}) {
  const { t } = useContext(LanguageContext);
  const { userData } = useContext(UserContext);
  const { niches, languageOptions, categoryOptions, linkTypeItems } =
    Constant();
  const { selectedTheme } = useContext(ThemeContext);

  const [isContentVisible, setIsContentVisible] = useState(null);
  const [articleData, setArticleData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [start, setStart] = useState(0);
  const [isYourselfHovered, setIsYourselfHovered] = useState(false);
  const [isAiHovered, setIsAiHovered] = useState(false);
  const [search, setSearch] = useState("");
  const contentRef = useRef(null);

  const handleClickOutside = (event) => {
    if (contentRef.current && !contentRef.current.contains(event.target)) {
      setIsContentVisible(null);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleContent = (index) => {
    setIsContentVisible((prev) => (prev === index ? null : index));
  };

  const ongoingRequest = useRef(null);
  const fetchArticles = (append = false, searchValue = "") => {
    const payload = {
      start,
      length: 5,
      search: {
        value: searchValue,
        filters: [
          {
            key: "STATUS",
            value: 0,
          },
        ],
      },
    };

    if (JSON.stringify(ongoingRequest.current) === JSON.stringify(payload)) {
      return;
    }

    setIsLoading(true);
    ongoingRequest.current = payload;

    const url = `/users/${userData?.uuid}/articles/search`;
    axiosInstance
      .post(url, payload)
      .then((response) => {
        const newArticles = response?.data?.data || [];
        setArticleData((prev) =>
          append ? [...prev, ...newArticles] : newArticles
        );
        setStart((prevStart) => prevStart + newArticles.length);
      })
      .catch(() => {
        toast.error(t("There was an error fetching the articles data"));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line
  const debouncedSearch = useCallback(
    _.debounce((searchTerm) => {
      fetchArticles(false, searchTerm);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSearch(search);
    return () => {
      debouncedSearch.cancel();
    };
  }, [search, debouncedSearch]);

  const hsInitRef = useRef(null);
  useEffect(() => {
    if (!hsInitRef.current) {
      HSCore.components.HSTomSelect.init(".js-select-search");
      // eslint-disable-next-line react-hooks/exhaustive-deps
      hsInitRef.current = true;
    }
  }, []);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      fetchArticles(true);
    }
  };

  const formatWordCount = (wordCount) => {
    if (!wordCount || wordCount === 0) {
      return `0 ${t("Words")}`;
    }
    if (wordCount === 1) {
      return `1 ${t("Word")}`;
    }
    return `${wordCount} ${t("Words")}`;
  };

  const renderNicheContent = (niche, nicheType) => {
    if (!niche?.passed || !nicheType)
      return (
        <span>
          <Loader />
        </span>
      );
    const matchedNiche = niches.find((n) => n.value === nicheType);
    return matchedNiche ? (
      <span className="article-table-tags bg-soft-secondary rounded-circle">
        {matchedNiche.content}
      </span>
    ) : null;
  };

  const renderMediaContent = (item) => {
    const { images, videos } = item?.quality || {};
    return (
      <div className="d-flex">
        <span
          className="d-flex align-items-center text-secondary"
          style={{
            background:
              selectedTheme === "dark" ? "#2d3236" : "rgb(113 134 157 / 10%)",
            borderRadius: "100px",
            width: "36px",
            height: "23px",
            fontSize: "12px",
            justifyContent: "left",
            paddingLeft: "4px",
            gap: "4px",
            border: "1.5px solid var(--bs-card-bg)",
            borderRight: "transparent",
          }}
        >
          {images}{" "}
          <i className="bi bi-card-image" style={{ color: "#61696f" }}></i>
        </span>
        <span
          className="d-flex align-items-center text-secondary position-relative z-index-10"
          style={{
            background:
              selectedTheme === "dark" ? "#2d3236" : "rgb(113 134 157 / 10%)",
            borderRadius: "100px",
            marginLeft: "-6px",
            width: "36px",
            height: "23px",
            fontSize: "12px",
            justifyContent: "center",
            border: "1.5px solid var(--bs-card-bg)",
            zIndex: "500",
          }}
        >
          {videos} <VidIcon />
        </span>
      </div>
    );
  };

  const readability = (readability) => {
    return (
      !readability?.passed && (
        <div className="d-flex justify-content-center gap-2 align-items-center">
          <span className="cross-icon">
            <i className="bi bi-x"></i>
          </span>
          <span className="custom-text-gray">{t("Readability")}</span>
        </div>
      )
    );
  };

  const renderContent = (content, label) => {
    if (!content)
      return (
        <span>
          <Loader />
        </span>
      );
    if (content?.passed) return null;

    return (
      <div className="d-flex justify-content-center gap-2 align-items-center">
        <span className="cross-icon">
          <i className="bi bi-x"></i>
        </span>
        <span className="custom-text-gray">{label}</span>
      </div>
    );
  };

  const ArticleItem = ({ item, color }) => {
    return (
      <span>
        <h1
          className={`m-0 text-wrap ${
            selectedTheme !== "dark"
              ? "text-article"
              : color
              ? "text-article-alt"
              : "text-article"
          }`}
          style={{ fontSize: "16px" }}
        >
          {item?.title}
        </h1>
        <div className="d-flex align-items-center gap-3 mt-2 flex-wrap">
          <span className="article-table-tags bg-soft-secondary text-secondary">
            {languageOptions[item?.quality?.language_type]}
          </span>
          <span className="article-table-tags bg-soft-secondary text-secondary">
            {categoryOptions[item?.quality?.category_type]}
          </span>
          <span className="d-flex justify-content-center align-items-center">
            {renderNicheContent(
              item?.quality?.niche,
              item?.quality?.niche_type
            )}
          </span>
          <span className="article-table-tags bg-soft-secondary text-secondary">
            {formatWordCount(item?.quality?.words)}
          </span>
          {renderMediaContent(item)}
          {renderContent(item?.quality?.ai, t("AI Detecting"))}
          {readability(item?.quality?.readability)}
          {renderContent(item?.quality?.plagiarism, t("Plagiarism"))}
          {renderContent(item?.quality?.seo, t("SEO"))}
        </div>
        <div className="d-flex align-content-center align-items-center gap-3 mt-2 flex-wrap">
          {item?.links?.slice(0, 3)?.map((link) => (
            <div className="d-flex align-items-center gap-2">
              {" "}
              <i className="bi bi-link-45deg"></i>{" "}
              <a
                href={link?.to}
                className={`link-font-style ${
                  link?.rel === 1 && "text-primary"
                }`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {link?.anchor}
              </a>
            </div>
          ))}
          {item?.links?.length > 3 && (
            <span className="link-font-style text-muted">
              +{item?.links?.length - 3} more
            </span>
          )}
        </div>
      </span>
    );
  };

  const handleSelectArticle = (cartItemUuid, articleUuid) => {
    const url = `/cart/items/${cartItemUuid}/${articleUuid}`;
    axiosInstance
      .put(url)
      .then(() => {
        getCartData();
        setIsContentVisible(null);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <div id="guestsSection" className="card mb-4">
      <div className="card-header d-flex justify-content-between pb-1 pt-4">
        <h4 className="card-title">{t("Guest Post(s)")}</h4>
        <p className="responsive-text-affiliation text-secondary" style={{ fontWeight: "700"}}>
          {t("Sub Total")}: ${guestTotal}
        </p>
      </div>
      <div className="table-responsive datatable-custom">
        <table className="table table-thead-bordered table-nowrap card-table responsive-table">
          <thead className="thead-light">
            <tr>
              <th className="pe-md-2 fw-bold border-unset">#</th>
              <th className="ps-md-3 fw-bold">{t("Website")}</th>
              <th className="fw-bold w-70">{t("Content")}</th>
              <th className="fw-bold">{t("Price")}</th>
              <th className="table-column-ps-0 fw-bold"></th>
            </tr>
          </thead>

          <tbody>
            {!!guestPosts?.length ? (
              guestPosts?.map((item, index) => (
                <>
                  <tr
                    key={index}
                    className={`responsive-row`}
                    style={{
                      position: window.innerWidth <= 768 ? "static" : "",
                    }}
                  >
                    <td className="display-row align-middle justify-content-between pt-2 pt-md-3 pe-md-2 pb-0 pb-sm-0 pb-md-6 fw-bold">
                      {index + 1}

                      <div className="kebab-menu-sm">
                        <div className="dropdowm float-end">
                          <button
                            type="button"
                            className="btn btn-ghost-secondary btn-icon btn-sm rounded-circle"
                            id="teamsDropdown2"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            <i className="bi-three-dots-vertical"></i>
                          </button>

                          <div
                            className="dropdown-menu dropdown-menu-sm dropdown-menu-end"
                            aria-labelledby="teamsDropdown2"
                          >
                            {item?.post?.article ? (
                              <button
                                className="dropdown-item d-flex align-items-center"
                                onClick={() => toggleContent(index)}
                              >
                                <i
                                  style={{
                                    marginLeft: "-2px",
                                    marginRight: "8px",
                                  }}
                                >
                                  <EditIcon />
                                </i>
                                {t("Edit")}
                              </button>
                            ) : (
                              <button
                                className="dropdown-item js-add-to-favorites"
                                onClick={() => toggleContent(index)}
                              >
                                <i className="bi bi-file-earmark-plus dropdown-item-icon"></i>
                                {t("Add Content")}
                              </button>
                            )}

                            <div className="dropdown-divider"></div>
                            <button
                              className="d-flex align-items-center dropdown-item text-danger"
                              onClick={() => handleDeleteCartItem(item?.uuid)}
                            >
                              <i
                                style={{
                                  marginLeft: "-2px",
                                  marginRight: "8px",
                                }}
                              >
                                <DeleteIcon />
                              </i>{" "}
                              {t("Delete")}
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="ps-md-3 pt-1 pt-md-3">
                      <div className="d-flex flex-column gap-2">
                        <div>
                          <span
                            className="h4 text-inherit"
                            style={{ fontSize: "16px", fontWeight: "700" }}
                          >
                            {item?.post?.offer?.domain?.domain}
                          </span>
                          <i className="bi bi-person-check ps-2 text-secondary fs-4"></i>
                        </div>
                        <div>
                          {item?.post?.offer?.domain?.categories?.length ? (
                            <span>
                              {item?.post?.offer?.domain?.categories
                                ?.slice(0, 2)
                                ?.map((category, i) => (
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: "600",
                                    }}
                                    key={i}
                                    className="badge bg-soft-secondary text-secondary me-1"
                                  >
                                    {categoryOptions[category]}
                                  </span>
                                ))}
                              {item?.post?.offer?.domain?.categories?.length >
                              2 ? (
                                <span className="badge bg-soft-secondary text-secondary">
                                  +{" "}
                                  {item?.post?.offer?.domain?.categories
                                    ?.length - 3}{" "}
                                  {t("...")}
                                </span>
                              ) : (
                                ""
                              )}
                            </span>
                          ) : (
                            <span
                              className="badge bg-soft-secondary text-secondary"
                              style={{ fontSize: "12px", fontWeight: "600" }}
                            >
                              {t("Any Category")}
                            </span>
                          )}
                        </div>
                        <div className="">
                          <small
                            className="text-secondary"
                            style={{ fontSize: "12px", fontWeight: "400" }}
                          >
                            {item?.post?.offer?.links} {t("links")}{" "}
                            &nbsp;&bull;&nbsp;{" "}
                            {linkTypeItems[item?.post?.offer?.type]}
                          </small>
                        </div>
                      </div>
                    </td>

                    {/* <td className={`${isContentVisible === index ? "" : "align-middle"}`}> */}
                    <td className="pt-1 pt-md-3">
                      {item?.post?.article ? (
                        <div className="w-auto">
                          <ArticleItem item={item?.post?.article} />
                        </div>
                      ) : (
                        <div
                          className="flex-column w-100"
                          style={{
                            display:
                              isContentVisible === index ? "none" : "block",
                          }}
                        >
                          <button
                            className={`btn btn-add-content ${
                              emptyContentError
                                ? "border-danger text-primary"
                                : "btn-outline-primary"
                            } align-items-center rounded d-flex justify-content-between gap-5 mt-md-4`}
                            onClick={() => toggleContent(index)}
                          >
                            {t("Add Content")}{" "}
                            <i className="bi-chevron-down ps-1 "></i>
                          </button>
                          {emptyContentError && (
                            <div
                              className="mt-2 text-danger"
                              style={{ fontSize: "12px" }}
                            >
                              {t("Please select content for this term")}
                            </div>
                          )}
                        </div>
                      )}
                      {isContentVisible === index && (
                        <div
                          id="content"
                          className={`article-content show px-2 `}
                          ref={contentRef}
                          style={{
                            background:
                              selectedTheme === "dark"
                                ? "rgba(41, 45, 48, 1)"
                                : "rgb(250 250 251)",
                          }}
                        >
                          <div className="d-flex gap-1 py-4 px-2 flex-wrap flex-lg-nowrap justify-content-between">
                            <div className="search-input-articles">
                              <div
                                className="input-group input-group-merge input-group-flush border pe-1"
                                style={{
                                  borderRadius: "5px",
                                  height: "35px",
                                  background:
                                    selectedTheme === "dark"
                                      ? "rgba(37, 40, 42, 1)"
                                      : "#fff",
                                }}
                              >
                                <div className="input-group-prepend input-group-text px-2">
                                  <i className="bi-search"></i>
                                </div>
                                <input
                                  id="datatableSearch"
                                  type="search"
                                  className="form-control custom-placeholder h-100"
                                  placeholder={t("Search Websites")}
                                  aria-label="Search Article"
                                  onChange={(e) => setSearch(e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="d-flex gap-3 flex-wrap flex-lg-nowrap pt-2 pt-md-0">
                              <button
                                className="btn btn-outline-primary d-flex justify-content-center align-items-center"
                                style={{
                                  borderRadius: "5px",
                                  height: "35px",
                                  width: "131px",
                                }}
                                onMouseEnter={() => setIsYourselfHovered(true)}
                                onMouseLeave={() => setIsYourselfHovered(false)}
                              >
                                <span
                                  className="pe-2"
                                  style={{ verticalAlign: "text-bottom" }}
                                >
                                  <WriteYourself
                                    color={
                                      isYourselfHovered ? "white" : "#387CFF"
                                    }
                                  />
                                </span>
                                {t("Write Yourself")}
                              </button>
                              <button
                                className="btn btn-outline-primary d-flex justify-content-center align-items-center"
                                style={{
                                  borderRadius: "5px",
                                  height: "35px",
                                  width: "125px",
                                }}
                                onMouseEnter={() => setIsAiHovered(true)}
                                onMouseLeave={() => setIsAiHovered(false)}
                              >
                                <span
                                  className="pe-2"
                                  style={{ verticalAlign: "text-bottom" }}
                                >
                                  <WriteWithAi
                                    color={isAiHovered ? "white" : "#387CFF"}
                                  />
                                </span>
                                {t("Write with AI")}
                              </button>
                            </div>
                          </div>
                          <div className="line-with-title">
                            <span
                              className="line"
                              style={{ marginLeft: "-8px" }}
                            ></span>
                            <span className="text-secondary" style={{fontSize:"12px", fontWeight:"600"}}>
                              {t("Take from Drafts")}
                            </span>
                            <span
                              className="line"
                              style={{ marginRight: "-8px" }}
                            ></span>
                          </div>
                          <div
                            className="articles-tab cursor-pointer article-scroll"
                            onScroll={handleScroll}
                            style={{ maxHeight: "410px" }}
                          >
                            {!!articleData?.length ? (
                              articleData.map((art, index) => (
                                <div
                                  key={art.uuid}
                                  className={`py-4 mx-2`}
                                  style={{
                                    borderBottom:
                                      index === (articleData?.length || 0) - 1
                                        ? ""
                                        : "2px solid rgba(109, 116, 123, 0.219)",
                                  }}
                                >
                                  <div
                                    className="w-auto"
                                    onClick={() =>
                                      handleSelectArticle(item?.uuid, art?.uuid)
                                    }
                                  >
                                    <ArticleItem item={art} color={true} />
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-center mt-3">
                                {t("No articles available")}
                              </div>
                            )}
                            {isLoading && (
                              <div className="text-center py-3">
                                <Loader />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </td>

                    <td className="price-sm pt-1 pb-0 justify-content-between">
                      <p style={{ color: "#919293" }}> {t("PRICE")}:</p>
                      <p style={{ fontSize: "16px", fontWeight: "700" }}>
                        {" "}
                        ${item?.post?.offer?.price}
                      </p>
                    </td>
                    <td className="price-tab text-dark">
                      <p style={{ fontSize: "16px", fontWeight: "700" }}>
                        {" "}
                        ${item?.post?.offer?.price}
                      </p>
                    </td>
                    <td className="table-column-ps-0 kebab-menu align-middle">
                      <div className="dropdowm">
                        <button
                          type="button"
                          className="btn btn-ghost-secondary btn-icon btn-sm rounded-circle"
                          id="teamsDropdown2"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          <i className="bi-three-dots-vertical fs-4"></i>
                        </button>

                        <div
                          className="dropdown-menu dropdown-menu-sm dropdown-menu-end"
                          aria-labelledby="teamsDropdown2"
                        >
                          {item?.post?.article ? (
                            <button
                              className="dropdown-item d-flex align-items-center"
                              onClick={() => toggleContent(index)}
                            >
                              <i
                                style={{
                                  marginLeft: "-2px",
                                  marginRight: "8px",
                                }}
                              >
                                <EditIcon />
                              </i>
                              {t("Edit")}
                            </button>
                          ) : (
                            <button
                              className="dropdown-item js-add-to-favorites"
                              onClick={() => toggleContent(index)}
                            >
                              <i className="bi bi-file-earmark-plus dropdown-item-icon"></i>
                              {t("Add Content")}
                            </button>
                          )}

                          <div className="dropdown-divider"></div>
                          <button
                            className="d-flex align-items-center dropdown-item text-danger"
                            onClick={() => handleDeleteCartItem(item?.uuid)}
                          >
                            <i
                              style={{
                                marginLeft: "-2px",
                                marginRight: "8px",
                              }}
                            >
                              <DeleteIcon />
                            </i>{" "}
                            {t("Delete")}
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                  {index !== guestPosts?.length - 1 && (
                    <tr>
                      <td
                        className="py-0"
                        colSpan="100%"
                        style={{ border: "transparent" }}
                      >
                        <div
                          style={{
                            width: "calc(100% - 2px)",
                            height: "2px",
                            backgroundColor:
                              selectedTheme === "dark"
                                ? "rgba(45, 50, 54, 1)"
                                : "rgb(157 157 157)",
                            margin: "0 auto",
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </>
              ))
            ) : (
              <tr>
                <td colSpan="100%">
                  <div
                    className="text-center p-4"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      textAlign: "center",
                    }}
                  >
                    <img
                      className="mb-3"
                      src="https://static.articlesfactory.ai/app/svg/illustrations-light/oc-error.svg"
                      alt=""
                      style={{ width: "10rem" }}
                      data-hs-theme-appearance="dark"
                    />
                    <img
                      className="mb-3"
                      src="https://static.articlesfactory.ai/app/svg/illustrations/oc-error.svg"
                      alt=""
                      style={{ width: "10rem" }}
                      data-hs-theme-appearance="default"
                    />
                    <p className="mb-0">
                      {!guestPosts ? "Loading data" : "No data to show"}
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default GuestPosts;
